'use client';

import customFetch, { getSessionTokens, removeSessionTokens, setSessionTokens } from '@/api/fetch';
import AuthContext from '@/context/AuthProvider';
import { getCookie } from '@/utils/cookies';
import { removeLocalStorage } from '@/utils/local-storage';
import { User } from '@/utils/types';
import { useContext } from 'react';

const allowedDDIs = [
  '+55', // Brasil
  '+1', // EUA e Canadá
  '+44', // Reino Unido
  '+49', // Alemanha
  '+33', // França
  '+34', // Espanha
  '+39', // Itália
  '+351', // Portugal
  '+31', // Holanda
  '+61', // Austrália
  '+64', // Nova Zelândia
  '+41', // Suíça
  '+47', // Noruega
  '+358', // Finlândia
  '+45', // Dinamarca
  '+46', // Suécia
  '+54', // Argentina
  '+56', // Chile
  '+57', // Colômbia
  '+316', // Países Baixos (Holanda)
  '+51', // Peru
  '+52', // México
  '+503', // Elsalvador
  '+507', //Panama
  '+1939', //Puerto Rico
  '+591', // Bolivia
  '+595', // Paraguai
  '+598', //Uruguai
  '+593', //Equador
  '+351', //Ilhas Belas
  '+597', // Surime
  '+598', //Uruguay
  '+58', //Venezuela
];

const verifyAllowedDDI = (phone_number: string) => {
  // LIBERADO TODOS OS PAISES A PEDIDO DO PAULO CESAR NO DIA 27.08.25 SOLICITADO VIA GRUPO DO WHATS
  // if (!allowedDDIs.some((ddi) => phone_number.startsWith(ddi))) {
  //   console.error('Blocked DDI', phone_number);
  //   return true;
  // }
  return false;
};

// Normaliza o role para os valores esperados pelo enum UserGroups
// A API pode retornar 'user' (singular) mas o enum usa 'users' (plural)
const normalizeUserRole = (role: string | undefined): string => {
  if (!role || role === 'user') {
    return 'users'; // UserGroups.USER = 'users'
  }
  return role;
};

const BRAZIL_DDI = '+55';

// DDI estrangeiro cai na defesa anti-fraude de SMS/WhatsApp (400800); nesses casos o e-mail é o caminho.
const isForeignDDI = (ddi?: string): boolean => !!ddi && ddi.trim() !== BRAZIL_DDI;

// Códigos de bloqueio da defesa anti-fraude de SMS/WhatsApp (ex.: 400800 = DDI estrangeiro).
const SMS_DEFENSE_ERROR_CODES = [400800, 400801, 400802, 400803, 400804, 400805, 400724];

// Bloqueio de defesa é um fallback tratado (login por e-mail), não uma falha: loga como warn
// para que o RUM não agregue esses eventos como erro. Qualquer outro erro continua sendo error.
const logAuthError = (message: string, error: any) => {
  const raw = error?.code ?? error?.error?.code;
  const code = typeof raw === 'string' ? parseInt(raw, 10) : raw;
  if (SMS_DEFENSE_ERROR_CODES.includes(code)) {
    console.warn(message, error);
  } else {
    console.error(message, error);
  }
};

export const useAuthV2 = () => {
  const { user, userLoaded, setUserLoaded, userData, setUserData, userGroups, sessionStatus, setAuthState, loadUser } =
    useContext(AuthContext);

  const handleSignUp = async (phone_number: string) => {
    if (verifyAllowedDDI(phone_number)) throw new Error('Blocked DDI');

    const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
    const captchaToken = getCookie('_grt') || null;
    const captchaAction = 'LOGIN';

    const signUpResponse = await customFetch('/signup', {
      method: 'POST',
      body: JSON.stringify({ phone: phone_number, captchaToken, siteKey, captchaAction }),
    });
    const { user: userSignUp } = signUpResponse;

    return userSignUp;
  };

  const handleSignUpConfirmation = async (username: string, SMSCode: string) => {
    const confirmSignUp = await customFetch('/confirm-signup', {
      method: 'POST',
      body: JSON.stringify({ phone: username, SMSCode }),
    });
    const { user, token } = confirmSignUp;

    if (token?.idToken || confirmSignUp._clientToken) {
      setSessionTokens({ accessToken: confirmSignUp._clientToken || token.idToken, refreshToken: '' });
    }

    return user;
  };

  const handleSignIn = async (
    phone_number: string,
    redirect = '/my-account'
  ): Promise<{
    [x: string]: any;
    userId: string;
    token: any;
    session: string;
    user: User;
    notConfirmed?: { status: string; message: string };
  }> => {
    if (verifyAllowedDDI(phone_number)) throw new Error('Blocked DDI');

    const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
    const captchaToken = getCookie('_grt') || null;
    const captchaAction = 'LOGIN';

    try {
      const signInResponse = await customFetch('/signin', {
        method: 'POST',
        body: JSON.stringify({ phone: phone_number, captchaToken, siteKey, captchaAction }),
      });

      if (signInResponse?.token?.idToken || signInResponse?._clientToken) {
        setSessionTokens({
          accessToken: signInResponse._clientToken || signInResponse.token.idToken,
          refreshToken: '',
        });
      }

      return { ...signInResponse };
    } catch (error: any) {
      if (error.code === 400650) {
        const errorData = error.error || error.authMethodRedirect || error;

        error.authMethodRedirect = {
          shouldRedirect: true,
          primaryAuthMethod: errorData.primaryAuthMethod || 'email',
          redirectTo: errorData.redirectTo || 'email',
          phone: errorData.phone,
          email: errorData.email,
          message: error.message || errorData.message,
        };
      }

      throw error;
    }
  };

  const handleConfirmSignIn = async (
    phone: string,
    SMSCode: string,
    Session: string
  ): Promise<{ userId: string; token: any; session: string; user: User }> => {
    const signInResponse = await customFetch('/signin', {
      method: 'POST',
      body: JSON.stringify({ phone, SMSCode, Session }),
    });

    if (signInResponse?.token?.idToken || signInResponse?._clientToken) {
      setSessionTokens({ accessToken: signInResponse._clientToken || signInResponse.token.idToken, refreshToken: '' });
    }

    return signInResponse;
  };

  const signOut = async () => {
    await customFetch('/signout', { method: 'POST' });

    setAuthState({
      user: null,
      userLoaded: false,
      userData: null,
      userGroups: [],
      sessionStatus: 'unauthenticated',
    });

    removeSessionTokens();
    removeLocalStorage('location');
    window.location.href = '/';
  };

  const isLoggedIn = () => {
    // Verifica o flag de estado de autenticação (não o token real)
    const authState = getSessionTokens()?.accessToken;
    return !!authState;
  };

  const handleSignInAdminV2 = async (email: string, password: string, isAdminWeb = true) => {
    const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
    const captchaToken = getCookie('_grt') || '';
    const captchaAction = 'LOGIN';

    const responseRole = await customFetch('/users/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, isAdminWeb, captchaToken, siteKey, captchaAction }),
    });

    const { user, step, session, cognitoChallangeSession, token, resetToken } = responseRole;

    await setLoadUserAdminV2(step === 'NEW_PASSWORD_REQUIRED' ? null : user, user.role);
    setSessionTokens({ accessToken: responseRole._clientToken || token, refreshToken: '' });

    return { user, step, session, cognitoChallangeSession, token, resetToken };
  };

  const setLoadUserAdminV2 = async (user: any, role?: string) => {
    const normalizedRole = normalizeUserRole(role);
    setAuthState({
      user,
      userLoaded: true,
      userData: user,
      userGroups: [normalizedRole],
      sessionStatus: 'authenticated',
    });
  };

  const handleChangePasswordInAdminV2 = async (email: string, session: string, newPassword: string) => {
    const confirmSignInResponse = await customFetch('/change-password', {
      method: 'POST',
      body: JSON.stringify({ email, session, newPassword }),
    });
    const { user, token } = confirmSignInResponse;

    // Atualizar contexto após troca de senha
    if (user) {
      const normalizedRole = normalizeUserRole(user.role);
      setAuthState({
        user,
        userLoaded: true,
        userData: user,
        userGroups: [normalizedRole],
        sessionStatus: 'authenticated',
      });
    }

    setSessionTokens({ accessToken: confirmSignInResponse._clientToken || token, refreshToken: '' });

    return { user, token };
  };

  const resendSignInCodeV2 = async (username: string) => {
    if (verifyAllowedDDI(username)) {
      throw new Error('Blocked DDI');
    }

    const resendSignInResponse = await customFetch('/resend-signin/' + username, {
      method: 'POST',
    });

    return { ...resendSignInResponse };
  };

  const sendEmailVerificationCode = async (
    email: string
  ): Promise<{ success: boolean; message: string; isNewUser?: boolean }> => {
    try {
      if (!email || !isValidEmail(email)) {
        throw new Error('Please enter a valid email address');
      }

      const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
      const captchaToken = getCookie('_grt') || null;
      const captchaAction = 'EMAIL_AUTH';

      const response = await customFetch('/auth/email/send-code', {
        method: 'POST',
        body: JSON.stringify({
          email,
          captchaToken,
          siteKey,
          captchaAction,
        }),
      });

      return { success: response.success, message: response.message, isNewUser: response.isNewUser };
    } catch (error: any) {
      logAuthError('Error sending email verification code:', error);

      if (error.code === 400650) {
        const errorData = error.error || error.authMethodRedirect || error;

        error.authMethodRedirect = {
          shouldRedirect: true,
          primaryAuthMethod: errorData.primaryAuthMethod || 'phone',
          redirectTo: errorData.redirectTo || 'phone',
          phone: errorData.phone,
          email: errorData.email,
          message: error.message || errorData.message,
        };
      }

      throw error;
    }
  };

  const verifyEmailCode = async (
    email: string,
    code: string,
    bypassBridgeCheck?: boolean
  ): Promise<{ userId: string; token: any; user: User; isNewUser: boolean }> => {
    try {
      if (!email || !isValidEmail(email)) {
        throw new Error('Please enter a valid email address');
      }

      if (!code || !isValidCode(code)) {
        throw new Error('Please enter a valid verification code');
      }

      const body: Record<string, unknown> = { email, code };
      if (bypassBridgeCheck) body.bypassBridgeCheck = true;

      const response = await customFetch('/auth/email/verify-code', {
        method: 'POST',
        body: JSON.stringify(body),
      });

      if (response?.token?.idToken || response?._clientToken) {
        setSessionTokens({
          accessToken: response._clientToken || response.token.idToken,
          refreshToken: response.token?.refreshToken || '',
        });
      }

      return {
        userId: response.user.id,
        token: response.token,
        user: response.user,
        isNewUser: response.isNewUser,
      };
    } catch (error: any) {
      logAuthError('Error verifying email code:', error);
      throw error;
    }
  };

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidCode = (code: string): boolean => {
    const codeRegex = /^\d{4,6}$/;
    return codeRegex.test(code);
  };

  const isValidPhone = (phone: string): boolean => {
    const cleanPhone = phone.replace(/\D/g, '');
    return cleanPhone.length >= 10 && cleanPhone.length <= 15;
  };

  const sendWhatsAppCode = async (
    phone: string
  ): Promise<{
    user?: User;
    phone: string;
    sentToPhone: string;
    isNewUser: boolean;
  }> => {
    try {
      if (!phone || !isValidPhone(phone)) {
        throw new Error('Please enter a valid phone number');
      }

      if (verifyAllowedDDI(phone)) {
        throw new Error('Blocked DDI');
      }

      const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
      const captchaToken = getCookie('_grt') || null;
      const captchaAction = 'WHATSAPP_AUTH';

      const response = await customFetch('/auth/whatsapp/send-code', {
        method: 'POST',
        body: JSON.stringify({
          phone,
          captchaToken,
          siteKey,
          captchaAction,
        }),
      });

      return response;
    } catch (error: any) {
      logAuthError('Error sending WhatsApp verification code:', error);

      if (error.code === 400650) {
        const errorData = error.error || error.authMethodRedirect || error;

        error.authMethodRedirect = {
          shouldRedirect: true,
          primaryAuthMethod: errorData.primaryAuthMethod || 'phone',
          redirectTo: errorData.redirectTo || 'phone',
          phone: errorData.phone,
          email: errorData.email,
          message: error.message || errorData.message,
        };
      }

      throw error;
    }
  };

  const verifyWhatsAppCode = async (
    phone: string,
    code: string,
    name?: string
  ): Promise<{ userId: string; token: any; user: User; isNewUser: boolean }> => {
    try {
      if (!phone || !isValidPhone(phone)) {
        throw new Error('Please enter a valid phone number');
      }

      if (!code || !isValidCode(code)) {
        throw new Error('Please enter a valid verification code');
      }

      const response = await customFetch('/auth/whatsapp/validate-code', {
        method: 'POST',
        body: JSON.stringify({ phone, code, name }),
      });

      const tokenValue =
        response?._clientToken || (typeof response?.token === 'string' ? response.token : response?.token?.idToken);
      if (tokenValue) {
        setSessionTokens({
          accessToken: tokenValue,
          refreshToken: '',
        });
      }

      return {
        userId: response.user?.id || response.userId || '',
        token: response.token,
        user: response.user || null,
        isNewUser: response.isNewUser,
      };
    } catch (error: any) {
      logAuthError('Error verifying WhatsApp code:', error);
      throw error;
    }
  };

  const resendWhatsAppCode = async (phone: string) => {
    if (verifyAllowedDDI(phone)) {
      throw new Error('Blocked DDI');
    }

    const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
    const captchaToken = getCookie('_grt') || null;
    const captchaAction = 'WHATSAPP_AUTH';

    const response = await customFetch('/auth/whatsapp/send-code', {
      method: 'POST',
      body: JSON.stringify({
        phone,
        captchaToken,
        siteKey,
        captchaAction,
      }),
    });

    return response;
  };

  const getAuthenticationMethods = () => {
    return {
      sms: {
        available: true,
        sendCode: handleSignIn,
        verifyCode: handleConfirmSignIn,
        resendCode: resendSignInCodeV2,
        label: 'SMS',
        icon: '📱',
      },
      email: {
        available: true,
        sendCode: sendEmailVerificationCode,
        verifyCode: verifyEmailCode,
        label: 'Email',
        icon: '✉️',
      },
      whatsapp: {
        available: true,
        sendCode: sendWhatsAppCode,
        verifyCode: verifyWhatsAppCode,
        resendCode: resendWhatsAppCode,
        label: 'WhatsApp',
        icon: '💬',
      },
    };
  };

  // Bridge Verification methods
  const sendBridgeVerification = async (
    email: string,
    phone: string,
    verificationMethod: 'whatsapp' | 'sms'
  ): Promise<{
    existingUserFound: boolean;
    codeSent: boolean;
    maskedPhone: string | null;
    bridgeToken: string | null;
  }> => {
    if (verifyAllowedDDI(phone)) throw new Error('Blocked DDI');

    const response = await customFetch('/auth/bridge-verification', {
      method: 'POST',
      body: JSON.stringify({ email, phone, verificationMethod }),
    });

    return response;
  };

  const confirmBridgeVerification = async (
    bridgeToken: string,
    code: string
  ): Promise<{
    user: User;
    token: { idToken: string; accessToken: string; refreshToken: string };
    linked: boolean;
  }> => {
    const response = await customFetch('/auth/bridge-verification/confirm', {
      method: 'POST',
      body: JSON.stringify({ bridgeToken, code }),
    });

    if (response?.token?.idToken || response?._clientToken) {
      setSessionTokens({
        accessToken: response._clientToken || response.token.idToken,
        refreshToken: response.token?.refreshToken || '',
      });
    }

    return response;
  };

  const RATE_LIMIT_ERROR_CODE = [400806, 400806, 400668, 400669, 400724];

  const getAuthErrorMessage = (error: any, method: 'sms' | 'email' | 'whatsapp'): string => {
    if (error?.code) {
      // SMS Defense errors - these have translations in errors.json
      if (SMS_DEFENSE_ERROR_CODES.includes(error.code)) {
        // Return the code as string so the caller can use t(`errors:${code}`)
        return String(error.code);
      }

      // Rate limit error with retryAfter
      if (RATE_LIMIT_ERROR_CODE.includes(error.code)) {
        return String(error.code);
      }

      switch (error.code) {
        case 400660:
        case 400661:
          return 'reCAPTCHA verification failed';
        case 242424:
          if (method === 'sms') return 'SMS service temporarily unavailable';
          if (method === 'email') return 'Email service temporarily unavailable';
          return 'WhatsApp service temporarily unavailable';
        case 400671:
          return 'Too many attempts, please try again later';
        case 'INVALID_CODE':
          return 'Invalid verification code';
        case 'CODE_EXPIRED':
        case 'EXPIRED_CODE':
          return 'Verification code has expired';
        case 'CODE_ATTEMPTS_EXCEEDED':
          return 'Too many incorrect attempts';
        case 'PHONE_REQUIRED':
          return 'Phone number is required';
        case 'UNKNOWN_ERROR':
          return 'WhatsApp service not available';
        case 400906:
          return String(error.code);
        case 400907:
          return String(error.code);
        default:
          return error.message || `${method.toUpperCase()} authentication failed`;
      }
    }
    return error.message || `${method.toUpperCase()} authentication failed`;
  };

  const shouldSuggestFallback = (error: any, method: 'sms' | 'email' | 'whatsapp'): boolean => {
    // Check new API field for suggesting alternative auth
    if (error?.shouldSuggestEmailAuth === true) {
      return true;
    }

    // Check for rate limit errors
    if (error?.status === 429 || error?.code === 'RATE_LIMIT_EXCEEDED' || error?.errorCode === 'RATE_LIMIT_EXCEEDED') {
      return true;
    }

    if (method === 'sms') {
      // SMS Defense errors should suggest email fallback
      if (SMS_DEFENSE_ERROR_CODES.includes(error?.code)) {
        return error?.shouldSuggestEmailAuth === true;
      }
      return error?.code === 242424 || error?.code === 400671 || error?.message?.includes('SMS');
    } else if (method === 'email') {
      return error?.code === 'EMAIL_SERVICE_UNAVAILABLE' || error?.message?.includes('email');
    } else {
      return error?.code === 'UNKNOWN_ERROR' || error?.message?.includes('WhatsApp');
    }
  };

  const isSMSDefenseError = (errorCode: number): boolean => {
    return SMS_DEFENSE_ERROR_CODES.includes(errorCode);
  };

  const isRateLimitError = (errorCode: number): boolean => {
    return RATE_LIMIT_ERROR_CODE.includes(errorCode);
  };

  const sendDependentCode = async (code: string): Promise<{ user: User; sentToEmail: string }> => {
    try {
      const response = await customFetch('/auth/code', {
        method: 'POST',
        body: JSON.stringify({ code }),
      });

      return {
        user: response.user,
        sentToEmail: response.sentToEmail,
      };
    } catch (error: any) {
      logAuthError('Error sending dependent code:', error);
      throw error;
    }
  };

  const verifyDependentCode = async (
    code: string,
    verificationCode: string
  ): Promise<{ userId: string; token: any; user: User }> => {
    try {
      const response = await customFetch('/auth/code/validate', {
        method: 'POST',
        body: JSON.stringify({ dependentCode: code, code: verificationCode }),
      });

      if (response?.token?.idToken || response?._clientToken) {
        setSessionTokens({
          accessToken: response._clientToken || response.token.idToken,
          refreshToken: '',
        });
      }

      return {
        userId: response.user.id,
        token: response.token,
        user: response.user,
      };
    } catch (error: any) {
      logAuthError('Error verifying dependent code:', error);
      throw error;
    }
  };

  // Seta o user diretamente no contexto sem fazer requisição HTTP
  // Útil quando já temos o user retornado pela API de confirmação
  const setUserDirectly = (user: User) => {
    setSessionTokens({ accessToken: 'authenticated', refreshToken: '' });
    const normalizedRole = normalizeUserRole(user?.role);
    setAuthState({
      user,
      userLoaded: true,
      userData: user,
      userGroups: [normalizedRole],
      sessionStatus: 'authenticated',
    });
  };

  return {
    user,
    userLoaded,
    sessionStatus,
    setUserLoaded,
    loadUser,
    setUserDirectly,
    isLoggedIn,
    userData,
    setUserData,
    userGroups,
    handleSignUp,
    handleSignUpConfirmation,
    handleSignIn,
    signOut,
    handleSignInAdminV2,
    handleConfirmSignIn,
    handleChangePasswordInAdminV2,
    resendSignInCodeV2,
    sendEmailVerificationCode,
    verifyEmailCode,
    sendWhatsAppCode,
    verifyWhatsAppCode,
    resendWhatsAppCode,
    getAuthenticationMethods,
    getAuthErrorMessage,
    shouldSuggestFallback,
    isValidEmail,
    isValidCode,
    isValidPhone,
    sendBridgeVerification,
    confirmBridgeVerification,
    isSMSDefenseError,
    isRateLimitError,
    isForeignDDI,
    sendDependentCode,
    verifyDependentCode,
  };
};
